import { Fragment, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import {
    Box, ListItemText, useMediaQuery, useTheme
} from "@mui/material";
import {
    Report as ReportIcon,
    Add as AddIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import {
    orange,
    pink
} from "@mui/material/colors";

import {
    FilledButton, Typography, IconButton, CheckboxListItemButton,
    CriticalOrSiteSpecificHazardListItemReadOnly, PlanRegisterHazardClassListItemReadOnly,
    PrestartHazardListItem,
    useSnackbar,
    BasicLoader,
} from "@initia-digital/component-library";
import { useMSALPayload } from "@initia-digital/auth-client-utils";
import { baseDefinedAndNotNullNarrowing, S3 } from "@initia-digital/ts-utils";
import { compareHazardsByRiskLevel, isCriticalOrSiteSpecificHazard } from "@initia-digital/data-service-mapping";
import { FileUploadStatus, HazardObservationMediaType, ICriticalOrSiteSpecificHazard, IHazardMedia, IPrestartHazard } from "@initia-digital/types/work-and-safety";

import { useWorkAndSafetyStore } from "../store";
import { useHazardClassList, usePlanDetail } from "../hooks";
import { sortHazardClassesByPresentationOrder, sortPlanRegisterHazardClassPresentationOrder } from "../utils";
import { putS3Object } from "../api/objectStorage";
import { IPrestartStageProps } from "../interfaces/componentInterfaces";
import { DEFAULT_APP_RESIZE_OPTIONS, DEFAULT_WEB_RESIZE_OPTIONS, loadFileUrl, scaleImage } from "../utils/files";


export const DiscussionTab = ({ handleSave }: IPrestartStageProps) => {
    // ------------------------------------------------
    const [hazardIdWithFileLoading, setHazardIdWithFileLoading] = useState<string | null>(null);

    const {
        editPrestartState, updateEditPrestartState
    } = useWorkAndSafetyStore();
    const {
        leadingTeam,
        teamAwareOfHighlightedHazards,
        needToAddTeamsHighlightedHazards,
        teamsHighlightedHazards
    } = editPrestartState;

    const params = useParams();
    const { jobId, planId, prestartId } = params;

    const theme = useTheme();
    const isSmallerThanSm = useMediaQuery(theme.breakpoints.down('sm'));

    const { queueSnackbar } = useSnackbar();

    // AUTH AND DATA INTEGRATION
    // ---------------------
    const authDetail = useMSALPayload();

    // PLAN DETAIL
    const { planDetailData } = usePlanDetail(authDetail, planId!, baseDefinedAndNotNullNarrowing(planId));
    const { criticalHazards, hazardsFromRegister } = planDetailData ?? { criticalHazards: [], hazardsFromRegister: [] };

    const { hazardClassListData } = useHazardClassList(authDetail, true, { status: 'ACTIVE' });
    const hazardClassListAreDefined = baseDefinedAndNotNullNarrowing(hazardClassListData);

    const referenceHazardClassPresentationOrder = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return [...hazardClassListData].sort(sortHazardClassesByPresentationOrder).map(hazardClass => hazardClass.label);
    }, [hazardClassListAreDefined, hazardClassListData]);

    const registerHazardsForPresentation = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined || !baseDefinedAndNotNullNarrowing(planDetailData)) return [];
        return [...planDetailData.hazardsFromRegister]
            .sort((a, b) => compareHazardsByRiskLevel(a, b))
            .sort((a, b) => sortPlanRegisterHazardClassPresentationOrder(a, b, referenceHazardClassPresentationOrder));
    }, [hazardClassListAreDefined, planDetailData, referenceHazardClassPresentationOrder]);

    const hazardClassGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return {};
        return Object.groupBy(registerHazardsForPresentation, (hazard) => hazard.hazardClass.label);
    }, [registerHazardsForPresentation, hazardClassListAreDefined]);

    const registerHazardGroups = useMemo(() => {
        // ---------------------
        if (!hazardClassListAreDefined) return [];
        return Object.keys(hazardClassGroups);
    }, [hazardClassGroups, hazardClassListAreDefined]);

    const hazardObservationMediaGroupsByHazardId = useMemo(() => {
        // ---------------------
        if (!baseDefinedAndNotNullNarrowing(editPrestartState)) return {} as Record<string, IHazardMedia[]>;
        return Object.groupBy(editPrestartState.hazardObservationMedia, (hazardObservationMedia) => hazardObservationMedia?.hazardId || 'noHazardId');
    }, [editPrestartState.hazardObservationMedia]);


    const loadFileUrlCb = useCallback((
        image: { filename: string } & IHazardMedia
    ) => loadFileUrl(image, authDetail, queueSnackbar), [authDetail, queueSnackbar]);

    const handleHazardObservationMediaUpload = useCallback(async (
        hazard: IPrestartHazard | ICriticalOrSiteSpecificHazard,
        files: File[]
    ) => {
        // ---------------------
        const newHazardObservationMediaList: IHazardMedia[] = [];
        for (const file of files) {
            // -----------------------------------------
            setHazardIdWithFileLoading(hazard.id);
            try {
                // ------------------------------
                const fileId = uuidv4();
                const path = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/original/${S3.toSafePath(file.name)}`;
                const appPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/app/${S3.toSafePath(file.name)}`;
                const webPath = `${import.meta.env.VITE_ENV_LOWER}-work-and-safety/${jobId}/prestart/hazard-observation/${hazard.id}/hazard-observation-media-${fileId}/web/${S3.toSafePath(file.name)}`;

                let hazardMediaType: HazardObservationMediaType = 'NEW_OBSERVATION';
                if (isCriticalOrSiteSpecificHazard(hazard)) {
                    // ------------------------------
                    hazardMediaType = 'HIGHLIGHTED';
                }

                const newHazardObservationMedia = {
                    id: fileId,
                    type: hazardMediaType,
                    hazardId: hazard.id ?? '',
                    hazardName: hazard.hazardName ?? '',
                    jobId: (jobId as string),
                    planId: (planId as string),
                    prestartId: '',
                    path: path,
                    name: file.name,
                    notes: ``,
                    size: file.size,
                    fileType: file.type,
                    file,
                    uploadStatus: "PENDING" as FileUploadStatus,
                    includeInAppendix: false
                };

                // UPLOAD ORIGINAL TO S3
                await putS3Object(authDetail, {
                    file,
                    path
                });

                // Add file to data state
                newHazardObservationMediaList.push(newHazardObservationMedia);

                // HANDLE RESIZED IMAGE OPTIONS TOO
                const appFile = await scaleImage(file, DEFAULT_APP_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: appFile,
                    path: appPath
                });
                const webFile = await scaleImage(file, DEFAULT_WEB_RESIZE_OPTIONS);
                await putS3Object(authDetail, {
                    file: webFile,
                    path: webPath
                });
            } catch (err) {
                // ------------------------------
                console.error(err);
                queueSnackbar(`There was an error uploading ${file.name}`, 'error', 'Error', 3000, '1rem');
            }
        }
        setHazardIdWithFileLoading(null);
        updateEditPrestartState({
            ...editPrestartState,
            hazardObservationMedia: [
                ...editPrestartState.hazardObservationMedia,
                ...newHazardObservationMediaList
            ]
        });
        // Save the plan with file uploads
        // - This has been added initially to match with aniticipated user expectations
        await handleSave({
            ...editPrestartState,
            hazardObservationMedia: [...editPrestartState.hazardObservationMedia, ...newHazardObservationMediaList]
        });
    }, [editPrestartState, handleSave, jobId, planId, prestartId, queueSnackbar]);

    const handleHazardObservationMediaDelete = useCallback(async (
        file: IHazardMedia
    ) => {
        // ---------------------
        updateEditPrestartState({
            ...editPrestartState,
            hazardObservationMedia: editPrestartState.hazardObservationMedia.filter(media => media.id !== file.id)
        });
    }, [editPrestartState]);


    return (
        <Box display="flex" flexDirection="column" my={isSmallerThanSm ? "0px" : "25px"}>
            <CheckboxListItemButton
                isSelected={teamAwareOfHighlightedHazards}
                onSelect={() => {
                    // ---------------------
                    updateEditPrestartState({
                        ...editPrestartState,
                        teamAwareOfHighlightedHazards: !teamAwareOfHighlightedHazards
                    });
                }}
                label={
                    <ListItemText>
                        <Typography
                            fontSize={"0.9rem"}
                            fontWeight={300}
                            color={theme.palette.primary.contrastText}
                        >
                            {
                                leadingTeam ?
                                    `Have you reviewed our highlighted and register hazards with the team?`
                                    : `Have you reviewed the highlighted and register hazards from the plan?`
                            }
                        </Typography>
                    </ListItemText>
                }
            />
            <Box>
                {
                    criticalHazards.length === 0 && (
                        <Box
                            display="flex" flexDirection="row" alignItems="center"
                            my="5px" gap={1}
                            pl={isSmallerThanSm ? "32px" : "29px"}
                            pr={isSmallerThanSm ? "8px" : "29px"}
                        >
                            <ReportIcon sx={{ color: orange['500'] }} />
                            <Typography
                                fontSize={"0.75rem"} color={orange['500']}
                            >
                                No highlighted or site specific hazards recorded
                            </Typography>
                        </Box>
                    )
                }
                {
                    criticalHazards.length > 0
                    && (
                        <Box
                            display="flex" flexDirection="column"
                            my="5px"
                            p="4px"
                            sx={{
                                containerType: 'inline-size',
                                containerName: `critical-hazard-plan-summary`,
                                width: '100%'
                            }}
                        >
                            <Box
                                display="flex" flexDirection="row" gap={1} alignItems="center"
                                pb="5px"
                            >
                                <Typography
                                    color={theme.palette.secondary.main}
                                    fontSize="0.7rem"
                                    fontWeight={700}
                                >
                                    {`Highlighted or site specific hazards (${criticalHazards.length})`}
                                </Typography>
                            </Box>
                            <Box
                                display="flex" flexDirection="row" alignItems="center"
                                px="10px" pb="5px"
                            >
                                <Typography
                                    fontSize="0.65rem"
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    {`If it is safe to do so, add any photos which capture highlighted hazards`}
                                </Typography>
                            </Box>
                            {
                                criticalHazards.length > 0 &&
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: '10px',
                                        '@container critical-hazard-plan-summary (min-width: 700px)': {
                                            gridTemplateColumns: 'repeat(2, 1fr)'
                                        },
                                        '@container critical-hazard-plan-summary (max-width: 699px)': {
                                            gridTemplateColumns: '1fr'
                                        }
                                    }}
                                >
                                    {criticalHazards.map(criticalHazard => {
                                        // --------------------------
                                        return (
                                            <Fragment key={criticalHazard.id}>
                                                <CriticalOrSiteSpecificHazardListItemReadOnly

                                                    hazard={criticalHazard}
                                                    images={baseDefinedAndNotNullNarrowing(criticalHazard.id) ? (hazardObservationMediaGroupsByHazardId[criticalHazard.id] ?? []).map(media => ({ ...media, filename: media.name })) : []}
                                                    loadImage={loadFileUrlCb}
                                                    handleImageUpload={async (files) => {
                                                        await handleHazardObservationMediaUpload(criticalHazard, files);
                                                    }}
                                                    handleImageDelete={async (file) => {
                                                        await handleHazardObservationMediaDelete(file);
                                                    }}
                                                    showGalleryWhenCollapsed={true}
                                                    maxExpandedImageHeight={150}
                                                    maxCollapsedImageHeight={100}
                                                />
                                                {
                                                    hazardIdWithFileLoading === criticalHazard.id && (
                                                        <BasicLoader
                                                            label="Loading"
                                                            size={80}
                                                        />
                                                    )
                                                }
                                            </Fragment>
                                        )
                                    })}
                                </Box>
                            }
                        </Box>
                    )
                }
                {
                    !teamAwareOfHighlightedHazards && (
                        <>
                            {
                                hazardsFromRegister.length === 0 && (
                                    <Box
                                        display="flex" flexDirection="row" alignItems="center"
                                        my="5px" gap={1}
                                    >
                                        <ReportIcon sx={{ color: pink['500'] }} />
                                        <Typography
                                            fontSize={"0.75rem"} color={pink['500']}
                                        >
                                            No register hazards recorded
                                        </Typography>
                                    </Box>
                                )
                            }
                            {
                                hazardsFromRegister.length > 0
                                && (
                                    <Box
                                        display="flex" flexDirection="column"
                                        my="5px"
                                        p="4px"
                                        sx={{
                                            containerType: 'inline-size',
                                            containerName: `register-hazard-plan-summary`,
                                            width: '100%'
                                        }}
                                    >
                                        <Box
                                            display="flex" flexDirection="row" gap={1} alignItems="center"
                                            pb="5px"
                                        >
                                            <Typography
                                                color={theme.palette.secondary.main}
                                                fontSize="0.7rem"
                                                fontWeight={700}
                                            >
                                                {`Register hazard groups (${registerHazardGroups.length} group${registerHazardGroups.length === 1 ? '' : 's'}, ${hazardsFromRegister.length} hazard${hazardsFromRegister.length === 1 ? '' : 's'})`}
                                            </Typography>
                                        </Box>
                                        {
                                            hazardsFromRegister.length > 0 &&
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: '10px',
                                                    '@container register-hazard-plan-summary (min-width: 700px)': {
                                                        gridTemplateColumns: 'repeat(2, 1fr)'
                                                    },
                                                    '@container register-hazard-plan-summary (max-width: 699px)': {
                                                        gridTemplateColumns: '1fr'
                                                    }
                                                }}
                                            >
                                                {
                                                    registerHazardGroups.map(hazardClassLabel => {
                                                        // ---------------------
                                                        return (
                                                            <PlanRegisterHazardClassListItemReadOnly
                                                                key={`plan-summary-register-hazard-group-${hazardClassLabel}`}
                                                                hazardClassLabel={hazardClassLabel}
                                                                hazards={hazardClassGroups[hazardClassLabel] ?? []}
                                                            />
                                                        )
                                                    })
                                                }
                                            </Box>
                                        }
                                    </Box>
                                )
                            }
                        </>
                    )}

            </Box>
            {
                leadingTeam && (
                    <>
                        <CheckboxListItemButton
                            isSelected={needToAddTeamsHighlightedHazards}
                            onSelect={() => {
                                // ---------------------
                                updateEditPrestartState({
                                    ...editPrestartState,
                                    needToAddTeamsHighlightedHazards: !needToAddTeamsHighlightedHazards
                                });
                            }}
                            label={
                                <ListItemText>
                                    <Typography
                                        fontSize={"0.9rem"}
                                        fontWeight={300}
                                        color={theme.palette.primary.contrastText}
                                    >
                                        Are there any hazards to record from the plan review?
                                    </Typography>
                                </ListItemText>
                            }
                        />
                        {
                            needToAddTeamsHighlightedHazards &&
                            (
                                <Box>
                                    <Box>
                                        <Typography
                                            fontSize={"0.9rem"}
                                            fontWeight={700}
                                            color={theme.palette.primary.contrastText}
                                        >
                                            {`New hazards from plan review ${teamsHighlightedHazards.length > 0 ? `(${teamsHighlightedHazards.length})` : ``}`}
                                        </Typography>
                                    </Box>
                                    <Box
                                        lineHeight={0.8}
                                    >
                                        <Typography
                                            fontSize="0.75rem"
                                            fontWeight={300}
                                            color={theme.palette.primary.contrastText}
                                        >
                                            Add new hazards or controls from the plan review
                                        </Typography>
                                    </Box>
                                    <Box display="flex" flexDirection="row" my={1} pl={0.5}>
                                        <FilledButton
                                            startIcon={<AddIcon />}
                                            onClick={() => {
                                                // ---------------------
                                                updateEditPrestartState({
                                                    ...editPrestartState,
                                                    teamsHighlightedHazards: [
                                                        ...teamsHighlightedHazards,
                                                        {
                                                            id: uuidv4(),
                                                            type: 'TEAM_HIGHLIGHTED',
                                                            hazardName: '',
                                                            description: '',
                                                            hazardControls: [],
                                                            jobId: jobId ?? '',
                                                            planId: planId ?? '',
                                                            prestartId: prestartId ?? '',
                                                            tags: []
                                                        }
                                                    ]
                                                });
                                            }}
                                        >
                                            New
                                        </FilledButton>
                                    </Box>
                                </Box>
                            )
                        }
                        {
                            needToAddTeamsHighlightedHazards &&
                            teamsHighlightedHazards.length === 0 &&
                            <Box display="flex" flexDirection="row" justifyContent="center">
                                <Typography
                                    fontSize={"0.9rem"}
                                    fontWeight={300}
                                    color={theme.palette.primary.contrastText}
                                >
                                    No other team hazards recorded
                                </Typography>
                            </Box>
                        }
                        {
                            needToAddTeamsHighlightedHazards &&
                            teamsHighlightedHazards.length > 0 &&
                            <Box
                                display="flex" flexDirection="column" rowGap={1}
                            >
                                {
                                    teamsHighlightedHazards.map(teamHazard => {
                                        // --------------------------
                                        return (
                                            <Fragment key={teamHazard.id}>
                                                <PrestartHazardListItem                                                    
                                                    hazard={teamHazard}
                                                    actionButtons={[
                                                        <IconButton
                                                            size="small"
                                                            tooltipTitle="Delete hazard"
                                                            onClick={() => {
                                                                // ---------------------
                                                                updateEditPrestartState({
                                                                    ...editPrestartState,
                                                                    teamsHighlightedHazards: editPrestartState.teamsHighlightedHazards
                                                                        .filter(hazard => hazard.id !== teamHazard.id)
                                                                });
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    ]}
                                                    handleHazardChange={(updatedHazard) => {
                                                        // ---------------------
                                                        updateEditPrestartState({
                                                            ...editPrestartState,
                                                            teamsHighlightedHazards: editPrestartState.teamsHighlightedHazards.map(hazard =>
                                                                hazard.id === updatedHazard.id ? updatedHazard : hazard
                                                            )
                                                        });
                                                    }}
                                                    images={baseDefinedAndNotNullNarrowing(teamHazard?.id) ? (hazardObservationMediaGroupsByHazardId[teamHazard.id] ?? []).map(media => ({ ...media, filename: media.name })) : []}
                                                    loadImage={loadFileUrlCb}
                                                    handleImageUpload={async (files) => {
                                                        await handleHazardObservationMediaUpload(teamHazard, files);
                                                    }}
                                                    handleImageDelete={async (file) => {
                                                        await handleHazardObservationMediaDelete(file);
                                                    }}
                                                    showGalleryWhenCollapsed={true}
                                                    maxExpandedImageHeight={150}
                                                    maxCollapsedImageHeight={100}
                                                />
                                                {
                                                    hazardIdWithFileLoading === teamHazard.id && (
                                                        <BasicLoader
                                                            label="Loading"
                                                            size={80}
                                                        />
                                                    )
                                                }
                                            </Fragment>
                                        )
                                    })
                                }
                            </Box>
                        }
                    </>
                )}
        </Box>
    )
}